import React from 'react';
import { FormControl, FormHelperText, OutlinedInput, OutlinedInputProps } from '@mui/material';
import { styled } from '@mui/material/styles';

import { colors } from '../theme/tokens.colors';
import { radius } from '../theme/tokens.radius';

export type UIKitInputSize = 'medium' | 'small';

export interface UIKitInputProps extends Omit<OutlinedInputProps, 'classes'> {
  htmlId?: string;
  /** Label externo (arriba del input). Si no lo pasás, no se renderiza. */
  label?: string;
  /** Texto de ayuda debajo (opcional) */
  helperText?: React.ReactNode;
  /** Error (pinta borde + label + helper) */
  error?: boolean;
  /** Tamaño del input */
  size?: UIKitInputSize;
  /** Wrapper style */
  className?: string;
  style?: React.CSSProperties;
  /** Permite customizar el input root */
  inputClassName?: string;
  inputStyle?: React.CSSProperties;

  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
}

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'inline-flex',
  flexDirection: 'column',
  width: fullWidth ? '100%' : 'auto',
}));

const Label = styled('span', {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'isError',
})<{ uiSize: UIKitInputSize; isError?: boolean }>(({ theme, uiSize, isError }) => ({
  fontWeight: 400,
  fontSize: 14,
  lineHeight: 1.5,
  marginBottom: uiSize === 'small' ? 4 : 8,
  color: isError ? theme.palette.error.main : theme.palette.text.primary,
}));

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

// MUI's OutlinedInput renders su propio notchedOutline/input internos con
// clases nativas estables (.MuiOutlinedInput-notchedOutline/-input) - la
// versión con makeStyles los alcanzaba con el truco de referencia cruzada
// de JSS ("&:hover $notchedOutline"); acá se seleccionan directo por esas
// clases nativas de MUI (mismo patrón que documenta MUI para customizar
// OutlinedInput en v5).
const StyledOutlinedInput = styled(OutlinedInput, {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'isError' && prop !== 'uiMultiline',
})<{ uiSize: UIKitInputSize; isError?: boolean; uiMultiline?: boolean }>(
  ({ theme, uiSize, isError, uiMultiline }) => {
    const getHeight = () => {
      if (uiMultiline) {
        return 88;
      }
      return uiSize === 'small' ? 40 : 56;
    };

    return {
      borderRadius: radius.xs,
      backgroundColor: colors.specialColors.white,
      height: getHeight(),
      fontSize: uiSize === 'small' ? 14 : 16,
      padding: uiSize === 'small' ? '10px 12px' : '18px 12px',

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isError ? theme.palette.error.main : colors.neutralVariant[300],
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.neutral[500],
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: isError ? theme.palette.error.main : theme.palette.primary.main,
        borderWidth: 2,
      },

      '&.Mui-disabled': {
        backgroundColor: colors.neutralVariant[100],
        color: colors.neutralVariant[700],
        opacity: 1,
        cursor: 'not-allowed',
        padding: 0,
      },

      '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        borderColor: isError ? theme.palette.error.main : colors.neutralVariant[200],
      },

      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main,
      },

      '& .MuiOutlinedInput-input': {
        padding: uiSize === 'small' ? '10px 12px' : '18px 12px',
        maxHeight: '100%',

        '&::placeholder': {
          color: colors.neutral[500],
          opacity: 1,
        },

        '&.Mui-disabled': {
          WebkitTextFillColor: colors.neutralVariant[700],
        },
      },
    };
  }
);

const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'isError',
})<{ uiSize: UIKitInputSize; isError?: boolean }>(({ theme, uiSize, isError }) => ({
  marginTop: uiSize === 'small' ? 4 : 8,
  fontSize: 12, // cambiar tamaño para small si es necesario
  lineHeight: 1.4,
  color: isError ? theme.palette.error.main : theme.palette.text.secondary,
}));

const UIKitInput: React.FC<UIKitInputProps> = ({
  htmlId,
  label,
  helperText,
  error = false,
  size = 'medium',
  disabled = false,

  className,
  style,

  inputClassName,
  inputStyle,
  fullWidth,
  multiline,
  rows,
  ...rest
}) => (
  <Root fullWidth={!!fullWidth} className={className} style={style}>
    {label && (
      <Label uiSize={size} isError={!!error}>
        {label}
      </Label>
    )}

    <StyledFormControl variant="outlined" disabled={disabled} error={error} fullWidth={fullWidth}>
      <StyledOutlinedInput
        id={htmlId}
        uiSize={size}
        isError={!!error}
        uiMultiline={!!multiline}
        className={inputClassName}
        style={inputStyle}
        disabled={disabled}
        error={error}
        multiline={multiline}
        rows={rows}
        {...rest}
      />

      {helperText && (
        <StyledHelperText uiSize={size} isError={!!error}>
          {helperText}
        </StyledHelperText>
      )}
    </StyledFormControl>
  </Root>
);

export default UIKitInput;
